import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaPlus,
  FaSave,
  FaTrash,
  FaCheckCircle,
  FaUserShield,
  FaUserTie,
  FaRegListAlt,
} from "react-icons/fa";

const API = "https://nissi-india.com/api";

const TenderUnsuccess = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [contractId, setContractId] = useState("");
  const [tenderId, setTenderId] = useState("");

  const [tenderUnsuccessId, setTenderUnsuccessId] = useState(null);

  const [authorizedBy, setAuthorizedBy] = useState("");
  const [approvedBy, setApprovedBy] = useState("");

  const isAuthorized = !!authorizedBy;
  const isMdApproved = !!approvedBy;

  const isReadOnly = useMemo(() => isMdApproved, [isMdApproved]);

  const canSubmit = useMemo(() => {
    if (isAuthorized) return false;
    if (isMdApproved) return false;
    return true;
  }, [isAuthorized, isMdApproved]);

  const [form, setForm] = useState({
    emdStatus: null,
    emdDate: "",
    emdReason: "",
    remarks: "",
  });

  const [bidders, setBidders] = useState([
    { biddersName: "", amount: "", position: "" },
    { biddersName: "", amount: "", position: "" },
  ]);

  const [approveChecked, setApproveChecked] = useState(false);
  const [mdApproveChecked, setMdApproveChecked] = useState(false);

  const updateForm = (patch) => setForm((p) => ({ ...p, ...patch }));

  const getUserId = () => {
    const v =
      sessionStorage.getItem("loginuserid") ||
      localStorage.getItem("loginuserid") ||
      localStorage.getItem("userId") ||
      "";
    const n = Number(v || 0);
    return Number.isFinite(n) ? n : 0;
  };

  const loadView = async (tid, cid) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API}/tenderUnsuccess/view`, {
        params: { tenderId: tid, contractId: cid },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load");
        return;
      }

      const d = res.data.data || {};

      setTenderUnsuccessId(d.tenderUnsuccessId ?? null);
      setAuthorizedBy(d.authorizedBy || "");
      setApprovedBy(d.approvedBy || "");

      setForm({
        emdStatus: d.emdStatus ?? null,
        emdDate: d.emdDate || "",
        emdReason: d.emdReason || "",
        remarks: d.remarks || "",
      });

      const bid = Array.isArray(d.bidders) ? d.bidders : [];
      if (bid.length > 0) {
        setBidders(
          bid.map((x) => ({
            biddersName: x.biddersName || "",
            amount: x.amount || "",
            position: x.position || "",
          }))
        );
      } else {
        setBidders([
          { biddersName: "", amount: "", position: "" },
          { biddersName: "", amount: "", position: "" },
        ]);
      }

      setApproveChecked(!!d.authorizedBy);
      setMdApproveChecked(!!d.approvedBy);
    } catch (err) {
      console.error("tenderUnsuccess view error:", err);
      toast.error("Failed to load view");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cid = urlParams.get("contractId") || "";
    const tid = urlParams.get("tenderId") || "";

    setContractId(cid);
    setTenderId(tid);

    if (tid) loadView(tid, cid);
  }, []);

  const onBack = () => window.history.back();

  const validateBiddersForAddMore = () => {
    for (let i = 0; i < bidders.length; i++) {
      const r = bidders[i];
      if (!r.biddersName.trim()) return { ok: false, msg: "Please enter Bidders Name" };
      if (!String(r.amount).trim()) return { ok: false, msg: "Please Enter the Amount" };
      if (!r.position.trim()) return { ok: false, msg: "Please Enter the Position" };
    }
    return { ok: true };
  };

  const validateBeforeSubmit = () => {
    if (form.emdStatus === null) {
      toast.error("Select EMD refunded Yes/No");
      return false;
    }

    if (form.emdStatus === true && !form.emdDate) {
      toast.error("EMD Date is required when EMD refunded = Yes");
      return false;
    }

    if (form.emdStatus === false && !String(form.emdReason || "").trim()) {
      toast.error("Reason is required when EMD refunded = No");
      return false;
    }

    for (let i = 0; i < bidders.length; i++) {
      const r = bidders[i];
      const any = r.biddersName.trim() || String(r.amount).trim() || r.position.trim();
      if (!any) continue;

      if (!r.biddersName.trim()) {
        toast.error(`Row ${i + 1}: Please enter Bidders Name`);
        return false;
      }
      if (!String(r.amount).trim()) {
        toast.error(`Row ${i + 1}: Please Enter the Amount`);
        return false;
      }
      if (!r.position.trim()) {
        toast.error(`Row ${i + 1}: Please Enter the Position`);
        return false;
      }
    }

    return true;
  };

  const onAddMore = () => {
    if (isReadOnly) return;

    const v = validateBiddersForAddMore();
    if (!v.ok) {
      toast.error(v.msg);
      return;
    }

    setBidders((p) => [...p, { biddersName: "", amount: "", position: "" }]);
  };

  const onRemoveRow = (idx) => {
    if (isReadOnly) return;
    setBidders((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== idx)));
  };

  const onChangeBidder = (idx, patch) => {
    setBidders((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    if (!validateBeforeSubmit()) return;

    const userId = getUserId();
    if (!userId) return toast.error("loginuserid missing in storage");

    try {
      setSaving(true);

      const payload = {
        tenderId: Number(tenderId),
        tenderUnsuccessId: tenderUnsuccessId || null,
        userId,

        emdStatus: form.emdStatus,
        emdDate: form.emdStatus ? form.emdDate : "",
        emdReason: form.emdStatus ? "" : form.emdReason,

        remarks: form.remarks,

        bidders: bidders.map((x) => ({
          biddersName: x.biddersName,
          amount: x.amount,
          position: x.position,
        })),
      };

      const res = await axios.post(`${API}/tenderUnsuccess/save`, payload);

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved Successfully");

        const newId = res.data?.data?.tenderUnsuccessId;
        if (newId) setTenderUnsuccessId(newId);

        await loadView(tenderId, contractId);
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("tenderUnsuccess save error:", err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onAuthorizeApprove = async () => {
    if (isReadOnly || isAuthorized) return;

    if (!approveChecked) {
      toast.error("Please tick Authorized Approval checkbox");
      return;
    }

    if (!tenderUnsuccessId) {
      toast.error("Please save first before approving");
      return;
    }

    const userId = getUserId();
    if (!userId) return toast.error("loginuserid missing in storage");

    try {
      setLoading(true);

      const res = await axios.post(`${API}/tenderUnsuccess/authorize`, {
        tenderId: Number(tenderId),
        tenderUnsuccessId: Number(tenderUnsuccessId),
        userId,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Authorized Approved Successfully");
        await loadView(tenderId, contractId);
      } else {
        toast.error(res.data?.message || "Authorize failed");
      }
    } catch (err) {
      console.error("tenderUnsuccess authorize error:", err);
      toast.error("Authorize failed");
    } finally {
      setLoading(false);
    }
  };

  const onMdApprove = async () => {
    if (isReadOnly || isMdApproved) return;

    if (!isAuthorized) {
      toast.error("Authorized approval required before MD approval");
      return;
    }

    if (!mdApproveChecked) {
      toast.error("Please tick MD Approval checkbox");
      return;
    }

    if (!tenderUnsuccessId) {
      toast.error("TenderUnsuccessId missing");
      return;
    }

    const userId = getUserId();
    if (!userId) return toast.error("loginuserid missing in storage");

    try {
      setLoading(true);

      const res = await axios.post(`${API}/tenderUnsuccess/mdApprove`, {
        tenderId: Number(tenderId),
        tenderUnsuccessId: Number(tenderUnsuccessId),
        userId,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "MD Approved Successfully");
        await loadView(tenderId, contractId);
        window.history.back();
      } else {
        toast.error(res.data?.message || "MD approve failed");
      }
    } catch (err) {
      console.error("tenderUnsuccess mdApprove error:", err);
      toast.error("MD approve failed");
    } finally {
      setLoading(false);
    }
  };

  const cardClass = `rounded-2xl shadow-md border ${
    isDark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-200 text-gray-900"
  }`;

  const labelClass = `${isDark ? "text-gray-200" : "text-gray-700"} text-sm font-semibold`;

  const inputClass = `w-full px-3 py-2 border rounded-lg outline-none ${
    isDark
      ? "bg-gray-800 border-gray-700 text-white focus:ring-2 focus:ring-gray-600"
      : "bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-gray-300"
  } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`;

  const tableHeadClass = `text-xs font-bold uppercase ${
    isDark ? "bg-gray-800 text-gray-200" : "bg-gray-50 text-gray-700"
  }`;

  const sectionTitle = (icon, text) => (
    <div className="flex items-center gap-2 mb-3">
      <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>{icon}</span>
      <h2 className="text-base font-bold">{text}</h2>
    </div>
  );

  const RadioYesNo = ({ value, onChange, name, disabled }) => (
    <div className="flex items-center gap-6">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name={name}
          checked={value === true}
          onChange={() => onChange(true)}
          disabled={disabled}
        />
        <span>Yes</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name={name}
          checked={value === false}
          onChange={() => onChange(false)}
          disabled={disabled}
        />
        <span>No</span>
      </label>
    </div>
  );

  return (
    <ProjectPreOrderLayoutWrapper>
      <ToastContainer />
      <HeaderBar title="Tender Unsuccessfull" />

      {(loading || saving) && (
        <div className="mb-4 text-sm opacity-80">{saving ? "Saving..." : "Loading..."}</div>
      )}

      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${
            isDark ? "border-gray-700 text-white" : "border-gray-300 text-gray-900"
          } hover:opacity-90`}
        >
          <FaArrowLeft />
          Back
        </button>

        <div className="text-xs opacity-80">
          Contract ID: <span className="font-semibold">{contractId || "-"}</span> {" | "}
          Tender ID: <span className="font-semibold">{tenderId || "-"}</span> {" | "}
          Unsuccess ID: <span className="font-semibold">{tenderUnsuccessId || "-"}</span>
        </div>
      </div>

      <div className={`${cardClass} p-4 mb-6`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-lg font-bold flex items-center gap-2">
              <FaCheckCircle className="opacity-80" />
              Tender Unsuccessful Summary
            </div>
            <div className="text-sm opacity-80 mt-1">
              Save details → Authorized approval → MD approval. After MD approval page becomes view-only.
            </div>
          </div>

          <div className="flex gap-2">
            {isMdApproved ? (
              <span className="px-3 py-1 rounded-full text-xs font-semibold border">✅ MD Approved (View Only)</span>
            ) : isAuthorized ? (
              <span className="px-3 py-1 rounded-full text-xs font-semibold border">🛡 Authorized (MD Pending)</span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-semibold border">✍ Draft / Not Approved</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${cardClass} p-5`}>
          {sectionTitle(<FaRegListAlt />, "Tender Unsuccessful Details")}

          <div className="mb-6">
            <div className={labelClass}>Whether EMD refunded</div>
            <div className="mt-2">
              <RadioYesNo
                name="emd"
                value={form.emdStatus}
                disabled={isReadOnly}
                onChange={(v) => {
                  if (v === true) updateForm({ emdStatus: true, emdReason: "" });
                  else updateForm({ emdStatus: false, emdDate: "" });
                }}
              />
            </div>

            {form.emdStatus === true && (
              <div className="mt-3">
                <div className="text-xs font-semibold opacity-80 mb-1">EMD Date</div>
                <input
                  type="date"
                  value={form.emdDate}
                  disabled={isReadOnly}
                  onChange={(e) => updateForm({ emdDate: e.target.value })}
                  className={inputClass}
                />
              </div>
            )}

            {form.emdStatus === false && (
              <div className="mt-3">
                <div className="text-xs font-semibold opacity-80 mb-1">Reason</div>
                <textarea
                  value={form.emdReason}
                  disabled={isReadOnly}
                  onChange={(e) => updateForm({ emdReason: e.target.value })}
                  className={inputClass}
                  rows={3}
                  placeholder="Enter reason..."
                />
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className={labelClass}>Bidders Detail</div>

              <button
                type="button"
                disabled={isReadOnly}
                onClick={onAddMore}
                className={`px-3 py-2 rounded-xl text-white flex items-center gap-2 ${
                  isReadOnly ? "bg-gray-400 cursor-not-allowed" : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaPlus />
                Add More
              </button>
            </div>

            <div className={`overflow-x-auto rounded-xl border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <table className="min-w-full text-sm">
                <thead className={tableHeadClass}>
                  <tr>
                    <th className="px-3 py-2 text-left">Name of the Bidders</th>
                    <th className="px-3 py-2 text-left">Amount</th>
                    <th className="px-3 py-2 text-left">Position</th>
                    <th className="px-3 py-2 text-center w-16">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bidders.map((r, idx) => (
                    <tr key={idx} className={`${isDark ? "border-gray-800" : "border-gray-100"} border-t`}>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={r.biddersName}
                          disabled={isReadOnly}
                          onChange={(e) => onChangeBidder(idx, { biddersName: e.target.value })}
                          className={inputClass}
                          placeholder="Bidders name"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={r.amount}
                          disabled={isReadOnly}
                          onChange={(e) => onChangeBidder(idx, { amount: e.target.value })}
                          className={inputClass}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={r.position}
                          disabled={isReadOnly}
                          onChange={(e) => onChangeBidder(idx, { position: e.target.value })}
                          className={inputClass}
                          placeholder="1 / 2 / L1 / etc."
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {!isReadOnly && (
                          <button
                            type="button"
                            className="px-2 py-2 rounded-lg border hover:opacity-80"
                            onClick={() => onRemoveRow(idx)}
                            title="Remove row"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-xs opacity-70 mt-2">
              Rule: If you enter any value in a row, all 3 fields must be filled.
            </div>
          </div>

          <div className="mb-2">
            <div className={labelClass}>Analysis of Failure (Remarks)</div>
            <div className="mt-2">
              <textarea
                value={form.remarks}
                disabled={isReadOnly}
                onChange={(e) => updateForm({ remarks: e.target.value })}
                className={inputClass}
                rows={4}
                placeholder="Enter remarks..."
              />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              disabled={!canSubmit || saving}
              onClick={onSubmit}
              className={`px-5 py-2 rounded-xl text-white flex items-center gap-2 ${
                canSubmit ? `${buttonClass} ${hoverClass}` : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              <FaSave />
              Submit
            </button>
          </div>
        </div>

        <div className={`${cardClass} p-5`}>
          {sectionTitle(<FaUserShield />, "Approval Workflow")}

          <div className="space-y-4">
            <div className={`p-4 rounded-2xl border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold flex items-center gap-2">
                    <FaUserShield className="opacity-80" />
                    Authorized Approval
                  </div>
                  <div className="text-sm opacity-80 mt-1">
                    Save first. Once authorized, submit is disabled and MD approval can proceed.
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs opacity-70">Status</div>
                  <div className="text-sm font-semibold">{isAuthorized ? "Approved" : "Pending"}</div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={approveChecked}
                    disabled={isReadOnly || isAuthorized}
                    onChange={(e) => setApproveChecked(e.target.checked)}
                  />
                  <span className="text-sm">Authorized Approval</span>
                </label>

                <button
                  type="button"
                  disabled={isReadOnly || isAuthorized || !approveChecked}
                  onClick={onAuthorizeApprove}
                  className={`px-4 py-2 rounded-xl text-white flex items-center gap-2 ${
                    !isReadOnly && !isAuthorized && approveChecked
                      ? `${buttonClass} ${hoverClass}`
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <FaUserShield />
                  Approve
                </button>
              </div>
            </div>

            <div className={`p-4 rounded-2xl border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold flex items-center gap-2">
                    <FaUserTie className="opacity-80" />
                    MD Approval
                  </div>
                  <div className="text-sm opacity-80 mt-1">Enabled only after Authorized approval.</div>
                </div>

                <div className="text-right">
                  <div className="text-xs opacity-70">Status</div>
                  <div className="text-sm font-semibold">{isMdApproved ? "Approved" : "Pending"}</div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <label className={`flex items-center gap-2 ${!isAuthorized ? "opacity-60" : ""}`}>
                  <input
                    type="checkbox"
                    checked={mdApproveChecked}
                    disabled={isReadOnly || isMdApproved || !isAuthorized}
                    onChange={(e) => setMdApproveChecked(e.target.checked)}
                  />
                  <span className="text-sm">MD Approval</span>
                </label>

                <button
                  type="button"
                  disabled={isReadOnly || isMdApproved || !isAuthorized || !mdApproveChecked}
                  onClick={onMdApprove}
                  className={`px-4 py-2 rounded-xl text-white flex items-center gap-2 ${
                    !isReadOnly && !isMdApproved && isAuthorized && mdApproveChecked
                      ? `${buttonClass} ${hoverClass}`
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <FaUserTie />
                  MD Approve
                </button>
              </div>
            </div>

            <div className="text-xs opacity-70 leading-relaxed">
              <div className="font-semibold mb-1">Rules</div>
              <ul className="list-disc ml-5 space-y-1">
                <li>Submit disabled after Authorized approval.</li>
                <li>After MD approval, page becomes view-only.</li>
                <li>MD approval sets Tender Status = 0 (Unsuccessful).</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default TenderUnsuccess;